/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import Grid from "@mui/material/Grid";
import SoftBox from "components/SoftBox";
import DefaultPricingCard from "examples/Cards/PricingCards/DefaultPricingCard";
import { getCheckoutUrl } from "../stripePayments";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, collection, doc, getDocs, query, where } from "firebase/firestore";
import { getPortalUrl } from "../stripePayments";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import SoftTypography from "components/SoftTypography";



function PricingCards({ prices, isLoading, setIsLoading }) {
  const [small, starter, premium, enterprise] = prices;
  const [currentUserSubscription, setCurrentUserSubscription] = useState(null);
  const navigate = useNavigate();


  const promptLoginAlert = () => {
    Swal.fire({
      title: "Not Logged In",
      text: "You need to be logged in to perform this action.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Login',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/authentication/sign-in/basic'); 
      }
    });
  };

  useEffect(() => {
    const auth = getAuth();
    onAuthStateChanged(auth, (user) => {
      if (user) {
        const fetchUserSubscription = async () => {
          const db = getFirestore();
          const userSubDoc = collection(db, 'customerDetails', user.uid, 'subscriptions'); 
          const querySnapshot = await getDocs(query(userSubDoc, where('status', 'in', ['trialing', 'active'])));

          let latestSubscription = null;
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            if (!latestSubscription || data.current_period_start.toDate() > latestSubscription.current_period_start.toDate()) {
              latestSubscription = data;
            }
          });

          if (latestSubscription) {
            setCurrentUserSubscription(latestSubscription);
            console.log("Latest active/trialing subscription: ", latestSubscription);
          } else {
            console.log("No active/trialing subscriptions found.");
          }
        };
        fetchUserSubscription();
      }
    });
  }, []);


  const priceIdMapping = {
    Silver: "price_1P3kdWLiDfhhXJlFnroutVz5",
    Gold: "price_1P3kgVLiDfhhXJlFyAPLbcjy", 
    Platinum: "price_1PIu0ZLiDfhhXJlFNePzCIzm",  
  };

  const isActiveSubscription = currentUserSubscription && (currentUserSubscription.status === "active" || currentUserSubscription.status === "trialing");
  const activePlanId = isActiveSubscription ? currentUserSubscription.items[0].plan.id : null;

  // Initialize Firebase Auth
  const auth = getAuth();

  const handleJoinClick = async (priceId, enableTrial = false) => {
    if (!auth.currentUser) {
      promptLoginAlert();
      console.log("User not logged in");
      return;
    }
    setIsLoading(true);

    try {
      console.log("getting the checkoutUrl");
      const checkoutUrl = await getCheckoutUrl(priceId, enableTrial);
      window.location.href = checkoutUrl; // Redirect to Stripe Checkout
    } catch (error) {
      console.error("Error during checkout process:", error);
      setIsLoading(false);
    }
  };

  const manageSubscriptionClick = async () => {
    if (!auth.currentUser) {
      promptLoginAlert();
      console.log("User not logged in");
      return;
    }
    setIsLoading(true);

    try {
      const portalUrl = await getPortalUrl();
      window.location.href = portalUrl;
    } catch (error) {
      console.error("Error during managing subscription:", error);
      setIsLoading(false);
    }
  }

  const handleCardClick = (planName, priceId) => {
    if (isActiveSubscription) {
      manageSubscriptionClick();
    } else {
      handleJoinClick(priceId);
    }
  }

  const isCurrentPlan = (planName) => {
    //console.log("1: ", isActiveSubscription)
    //console.log("2: ", priceIdMapping[planName])
    //console.log("3: ", activePlanId)
    //console.log("current plan: ", isCurrentPlan)
    return isActiveSubscription && priceIdMapping[planName] === activePlanId;
  };

  return (
    <SoftBox position="relative" zIndex={10} mt={{ lg: -5, xl: -8, xxl: -9 }} px={{ xs: 1, sm: 0 }}>
      <Grid container spacing={3} justifyContent="center">
      <Grid item xs={12} lg={4}>
          <DefaultPricingCard
            badge={{ color: isCurrentPlan("Silver") ? "success" : "secondary", label: isCurrentPlan("Silver") ? "Silver (selected)" : "Silver"}}
            price={{ currency: "$", value:  <span>{small}<span style={{ fontSize: 'small' }}>/Month</span></span>}}
            specifications={[
              { label: "70 Recording Hours", includes: true, tooltip: "Each recording hour represents 60 minutes of audio uploaded for transcription and note generation. Unuploaded recordings are not counted towards this limit." },
              { label: "Default Note Templates", includes: true, tooltip: "More information below" },
              { label: "Unlimited Patients", includes: true },
              { label: "AI Assistant", includes: true },
              { label: "Make Notes Concise", includes: true },
              { label: "Manual Note Editing", includes: true },
              { label: "Basic Support", includes: true, tooltip: "More information below"},
              { label: "Customize Your Notes", includes: false },
              { label: "MDM Coding", includes: false },
              //{ label: "Complete documentation", includes: false },
            ]}
            action={{
              type: "external",
              href: "/",
              color: "info",
              label: isCurrentPlan("Silver") ? "Manage Plan" : (isCurrentPlan("Gold") ? "downgrade to Silver" : (isCurrentPlan("Platinum") ? "downgrade to Silver" : "Start Free Trial")),
            }}
            onClick={() => handleCardClick("Silver Subscription", "price_1P3kdWLiDfhhXJlFnroutVz5")}
          />
          <SoftBox pt={3} px={2} >
            <SoftTypography variant="body2" align="left" color="text" >
              For health providers that just need basics or are just getting started.
            </SoftTypography>
          </SoftBox>
        </Grid>
        <Grid item xs={12} lg={4}>
          <DefaultPricingCard
            badge={{ color: isCurrentPlan("Gold") ? "success" : "secondary", label: isCurrentPlan("Gold") ? "Gold (selected)" : "Gold" }}
            price={{ currency: "$", value: <span>{starter}<span style={{ fontSize: 'small' }}>/Month</span></span>}}
            specifications={[
              { label: "120 Recording Hours", includes: true, tooltip: "Each recording hour represents 60 minutes of audio uploaded for transcription and note generation. Unuploaded recordings are not counted towards this limit." },
              { label: "Customizable Notes", includes: true, tooltip: "More information below" },
              { label: "Re-Generate Notes", includes: true },
              { label: "Talk To Your Notes", includes: true },
              { label: "Email Sending To Patient", includes: true },
              { label: "Personal Task Planner", includes: true },
              { label: "Copy Templates", includes: true },
              { label: "Access To Our Libraries", includes: true },
              { label: "Premium Support", includes: true, tooltip: "More information below" },
              { label: "Unlimited Customization", includes: false },
              { label: "MDM Coding", includes: false },
              //{ label: "Complete documentation", includes: false },
            ]}
            action={{
              type: "external",
              href: "/",
              color: "dark",
              label: isCurrentPlan("Gold") ? "Manage Plan" : (isCurrentPlan("Platinum") ? "downgrade to Gold" : (isCurrentPlan("Silver") ? "Upgrade to Gold" : "Start Free Trial")),
            }}
            onClick={() => handleCardClick("Gold Subscription", 'price_1P3kgVLiDfhhXJlFyAPLbcjy')}
          />
          <SoftBox pt={3} px={2} >
            <SoftTypography variant="body2" align="left" color="text" >
                Ideal for health pros looking to tailor their tools to their personal needs.
            </SoftTypography>
          </SoftBox>
        </Grid>
        <Grid item xs={12} lg={4}>
          <DefaultPricingCard
            badge={{ color: isCurrentPlan("Platinum") ? "success" : "secondary", label: isCurrentPlan("Platinum") ? "Platinum (selected)" : "Platinum"}}
            price={{ currency: "$", value:  <span>{premium}<span style={{ fontSize: 'small' }}>/Month</span></span>}}
            specifications={[
              { label: "250 Recording Hours", includes: true, tooltip: "Each recording hour represents 60 minutes of audio uploaded for transcription and note generation. Unuploaded recordings are not counted towards this limit." },
              { label: "Access to all features", includes: true},
              { label: "Unlimited Customization", includes: true },
              { label: "MDM Coding + Suggestions", includes: true },
              { label: "Ultimate Support", includes: true, tooltip: "More information below" },
            ]}
            action={{
              type: "external",
              href: "/",
              color: "info",
              label: isCurrentPlan("Platinum") ? "Manage Plan" : (isCurrentPlan("Gold") ? "Upgrade to Platinum" : (isCurrentPlan("Silver") ? "Upgrade to Platinum" : "Start Free Trial")),
            }}
            onClick={() => handleCardClick("Platinum Subscription", "price_1PIu0ZLiDfhhXJlFNePzCIzm")}
          />
          <SoftBox pt={3} px={2} >
            <SoftTypography variant="body2" align="left" color="text" >
              Geared for solo practitioners who want the full suite: max hours and unlimited note templates.
            </SoftTypography>
          </SoftBox>
        </Grid>
        {/*<Grid item xs={12} lg={3}>
          <DefaultPricingCard
            badge={{ color: "secondary", label: "enterprise" }}
            price={{ currency: "$", value: enterprise }}
            specifications={[
              { label: "Unlimited team members", includes: true },
              { label: "100GB Cloud storage", includes: true },
              { label: "Integration help", includes: true },
              { label: "Sketch Files", includes: true },
              { label: "API Access", includes: true },
              { label: "Complete documentation", includes: true },
            ]}
            action={{
              type: "internal",
              route: "/",
              color: "dark",
              label: "join",
            }}
          />
          </Grid>*/}
      </Grid>
    </SoftBox>
  );
}

// Typechecking props for the PricingCards
PricingCards.propTypes = {
  prices: PropTypes.arrayOf(PropTypes.string).isRequired,
  currentUserSubscription: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  setIsLoading: PropTypes.func.isRequired,
};

export default PricingCards;
